import { Alert, Button, Form, Input } from 'antd';
import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../../store/store';

const { TextArea } = Input;

/** Soft email truncation threshold (backend cuts reason in the notification email only). */
const REASON_EMAIL_TRUNCATE_LENGTH = 500;

/**
 * Props for EnterOnBehalfOfConfirmationForm.
 */
interface EnterOnBehalfOfConfirmationFormProps {
  /** Optional explanatory content (e.g., warning) to display above the form. */
  content?: ReactNode;
  /** Callback triggered when the form is submitted with a reason. */
  onSubmit: (reason: string) => void;
  /** Optional callback for form cancellation. */
  onCancel?: () => void;
  /** Optional label for the confirm button (defaults to i18n 'confirm'). */
  confirmLabel?: string;
  /** Optional label for the cancel button (defaults to i18n 'cancel'). */
  cancelLabel?: string;
  /** Name for the textarea field (for form data, defaults to 'reason'). */
  textareaName?: string;
  /** Optional placeholder text for the textarea. */
  textareaPlaceholder?: string;
  /** Autofocus the textarea on mount (defaults to true). */
  autoFocus?: boolean;
}

/**
 * A reusable confirmation form for "On Behalf Of" impersonation actions,
 * requiring the user to enter a reason for auditing purposes.
 * Intended for inclusion within Ant Design modals.
 */
function EnterOnBehalfOfConfirmationForm({
  content,
  onSubmit,
  onCancel,
  confirmLabel,
  cancelLabel,
  textareaName = 'reason',
  textareaPlaceholder,
  autoFocus = true,
}: EnterOnBehalfOfConfirmationFormProps) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  // Shared modal loading state (prevents double-submits)
  const isModalLoading = useAppStore((s) => s.isModalLoading);

  /**
   * Triggered when user clicks "confirm" or submits the form.
   * Validates the input, then calls onSubmit with the reason.
   */
  const handleConfirm = useCallback(() => {
    form
      .validateFields()
      .then((values) => {
        onSubmit((values[textareaName] as string | undefined) ?? '');
      })
      .catch(() => {
        // Validation error, do nothing (AntD will show feedback)
      });
  }, [form, onSubmit, textareaName]);

  /**
   * Triggered when user clicks "cancel".
   * Resets the form and calls optional onCancel callback.
   */
  const handleCancel = useCallback(() => {
    form.resetFields();
    onCancel?.();
  }, [form, onCancel]);

  return (
    <div>
      {content}
      <Alert
        className="mt-4! mb-2!"
        showIcon
        title={t('modal.impersonateUser.emailNotice')}
        type="info"
      />
      <Form
        className="[&_.ant-form-item]:mb-2! [&_.ant-form-item-explain-error]:hidden!"
        disabled={isModalLoading}
        form={form}
        layout="vertical"
        onFinish={handleConfirm}
        size="large"
      >
        <Form.Item
          // ponytail: antd showCount sits absolute under TextArea; pad extra so hint clears "0/500"
          className="[&_.ant-form-item-extra]:pt-5"
          extra={
            <span className="text-gray-dark">
              {t('modal.impersonateUser.reasonTruncateHint')}{' '}
              {t('modal.impersonateUser.reasonEmptyHint')}
            </span>
          }
          label={t('modal.impersonateUser.reasonLabel')}
          name={textareaName}
        >
          <TextArea
            autoFocus={autoFocus}
            disabled={isModalLoading}
            placeholder={
              textareaPlaceholder || t('common.enterReason') || 'Enter reason for impersonation...'
            }
            rows={4}
            showCount={{
              formatter: ({ count }) => `${count} / ${REASON_EMAIL_TRUNCATE_LENGTH}`,
            }}
          />
        </Form.Item>
        {/* Action buttons aligned to the end */}
        <div className="mt-12 flex justify-end gap-2">
          <Button
            aria-label={cancelLabel || t('common.cancel')}
            loading={isModalLoading}
            onClick={handleCancel}
            size="middle"
          >
            {cancelLabel || t('common.cancel')}
          </Button>
          <Button
            aria-label={confirmLabel || t('common.confirm')}
            htmlType="submit"
            loading={isModalLoading}
            size="middle"
            type="primary"
          >
            {confirmLabel || t('common.confirm')}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default EnterOnBehalfOfConfirmationForm;
